import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import { fetchData } from 'services/api/fetchData';
import TwoFactorEnrollment from '../account/TwoFactorEnrollment';
import SecondaryButton from 'components/buttons/SecondaryButton';

// Personal preferences section that shows the two factor status of the user
const TwoFactorPreference: React.FC = () => {
    const { t } = useTranslation();
    const { handleLogout } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const { initializeModal, closeModal } = useModal();
    const [twoFactorConfirmed, setTwoFactorConfirmed] = useState<boolean | null>(null);
    const [twoFactorRequired, setTwoFactorRequired] = useState<boolean>(false);

    // Fetch the two factor status of the current user on mount
    useEffect(() => {
        const fetchTwoFactorStatus = async () => {
            try {
                const status = await fetchData({ apiUrl: 'get_two_factor_status', handleLogout });
                if (status) {
                    setTwoFactorConfirmed(status.two_factor_confirmed);
                    setTwoFactorRequired(status.two_factor_required);
                }
            } catch (error) {
                console.error('Fetching two factor status failed', error);
            }
        };
        fetchTwoFactorStatus();
    }, []);

    // Open the enrollment modal to link a new authenticator device
    const openRelinkModal = () => {
        initializeModal(
            <TwoFactorEnrollment
                requirePassword={true}
                onSuccess={() => {
                    closeModal();
                    setTwoFactorConfirmed(true);
                    setFloatingAlert({ type: 'success' });
                }}
            />,
            { title: t('personal_preferences.two_factor.header'), modalSize: 'small' }
        );
    };

    // Hide the section when two factor is not required and not configured
    if (!twoFactorRequired && !twoFactorConfirmed) {
        return null;
    }

    return (
        <div className='two-factor-preference'>
            <h5>{t('personal_preferences.two_factor.header')}</h5>
            <p>
                {twoFactorConfirmed
                    ? t('personal_preferences.two_factor.status_enabled')
                    : t('personal_preferences.two_factor.status_disabled')}
            </p>
            {twoFactorConfirmed && (
                <SecondaryButton
                    label='personal_preferences.two_factor.relink_button_label'
                    size='small'
                    onClick={openRelinkModal}
                />
            )}
        </div>
    );
};

export default TwoFactorPreference;
